import React from "react";

export default function DemoVoltageGauge({ value, vtr = 1, isLine = false, label }) {
    const SQRT3 = 1.73205080757;

    // Corrected logic for demo:
    // If isLine is true, we want the scale to be capable of showing 380V+ (~520V max).
    // We assume 'value' passed is ALREADY the line voltage (e.g. 380).
    const max = isLine ? 300 * SQRT3 * vtr : 300 * vtr;

    // Unlike original VoltageGauge, we do NOT multiply value by SQRT3 if isLine is true.
    // We assume value is already the correct magnitude we want to display.
    const realValue = (value ?? 0) * vtr;

    const greenStart = isLine ? 200 * SQRT3 * vtr : 200 * vtr;
    const greenEnd = isLine ? 240 * SQRT3 * vtr : 240 * vtr;

    const yellowEnd = isLine ? 260 * SQRT3 * vtr : 260 * vtr;

    const redEnd = max;

    // Gauge açıları (160°)
    const startAngle = 200;
    const endAngle = 340;
    const totalAngle = endAngle - startAngle;

    // Value → Angle
    function valueToAngle(val) {
        const ratio = Math.min(Math.max(val / max, 0), 1);
        return startAngle + ratio * totalAngle;
    }

    function describeArc(cx, cy, r, startAngle, endAngle, color, width) {
        const start = polarToCartesian(cx, cy, r, endAngle);
        const end = polarToCartesian(cx, cy, r, startAngle);

        const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

        return (
            <path
                d={`M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`}
                stroke={color}
                strokeWidth={width}
                fill="none"
                strokeLinecap="round"
            />
        );
    }

    function polarToCartesian(cx, cy, r, angle) {
        const rad = (angle * Math.PI) / 180.0;
        return {
            x: cx + r * Math.cos(rad),
            y: cy + r * Math.sin(rad),
        };
    }

    const needleAngle = valueToAngle(realValue);

    return (
        <div className="flex flex-col items-center p-2 bg-white rounded-xl shadow-sm border border-slate-200 w-full h-full">
            <svg width="160" height="100" viewBox="0 0 220 140">
                {/* SEGMENTS */}
                {describeArc(110, 120, 90, startAngle, valueToAngle(greenEnd), "#22c55e", 12)}
                {describeArc(110, 120, 90, valueToAngle(greenEnd), valueToAngle(yellowEnd), "#f59e0b", 12)}
                {describeArc(110, 120, 90, valueToAngle(yellowEnd), valueToAngle(redEnd), "#ef4444", 12)}

                {/* SCALE */}
                {[...Array(11)].map((_, i) => {
                    const angle = startAngle + (i / 10) * totalAngle;
                    const p1 = polarToCartesian(110, 120, 78, angle);
                    const p2 = polarToCartesian(110, 120, 90, angle);
                    return (
                        <line
                            key={i}
                            x1={p1.x}
                            y1={p1.y}
                            x2={p2.x}
                            y2={p2.y}
                            stroke="#9ca3af"
                            strokeWidth="2"
                        />
                    );
                })}

                {/* NEEDLE */}
                <g transform={`rotate(${needleAngle + 90} 110 120)`}>
                    <line
                        x1="110"
                        y1="120"
                        x2="110"
                        y2="40"
                        stroke="black"
                        strokeWidth="4"
                        strokeLinecap="round"
                    />
                    <circle cx="110" cy="120" r="6" fill="black" />
                </g>
            </svg>

            <div className="text-xl font-semibold text-slate-700">
                {value != null ? value.toFixed(1) : "-"}
            </div>
            <div className="text-xs text-slate-500 mt-1">{label}</div>
        </div>
    );
}
